import { css } from "@emotion/css";
import { Button } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ReactNode } from "react";
import { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import type { HelpPanelImage } from "~/components/OnboardingDialog/HelpPanel";
import type { OnboardingDialogProps } from "~/components/OnboardingDialog/OnboardingDialog";
import OnboardingDialog from "~/components/OnboardingDialog/OnboardingDialog";
import type { OnboardingDialogLayoutProps } from "~/components/OnboardingDialog/OnboardingDialogLayout";
import { OnboardingDialogLayout } from "~/components/OnboardingDialog/OnboardingDialogLayout";
import { PagedOnboardingDialogProgressTracker } from "./PagedOnboardingDialogProgressTracker";
interface PagedOnboardingDialogProps extends OnboardingDialogProps {
    showPageIndicator?: boolean;
    pages: PagedOnboardingDialogPageData[];
    defaultTitle?: ReactNode;
    onClosingDialog?: () => void;
    completeButtonText?: string;
    hidePreviousButton: boolean;
    hideCompleteButton: boolean;
    showProgressTracker?: boolean;
}
interface PageDataShared extends Omit<OnboardingDialogLayoutProps, "title" | "actions" | "children" | "busy"> {
    name: string;
    title?: ReactNode | string;
    progressTrackerLabel?: string;
    render: () => ReactNode;
    onCloseDialog?: (isCancelled?: boolean) => void;
    helpPanelHidden?: boolean;
    helpPanelContent?: ReactNode;
    helpPanelImage?: HelpPanelImage;
    helpPanelHeight?: 500 | 600;
    onPageDisplayed?: () => Promise<void>;
    isPageModelValid?: () => Promise<boolean>;
    isBusy?: boolean;
    customSecondaryButton?: (pageNavigation: PageNavigation) => ReactNode;
    alwaysShowCancelButton?: boolean;
    alwaysShowCloseButton?: boolean;
    onlyShowCloseButton?: boolean;
    disableNextButton?: boolean;
    hideCancelButton?: boolean;
    hideBackButton?: boolean;
    nextButtonText?: string;
}
type PageDataWithCustomButton = PageDataShared & {
    customPrimaryButton: ReactNode | ((pageNavigation: PageNavigation) => ReactNode);
    onMovingToNextPage?: never;
};
type PageDataWithNextHandler = PageDataShared & {
    onMovingToNextPage: () => Promise<void>;
    customPrimaryButton?: never;
};
type PageDataDefault = PageDataShared & {
    customPrimaryButton?: undefined;
    onMovingToNextPage?: undefined;
};
export type PagedOnboardingDialogPageData = PageDataWithCustomButton | PageDataWithNextHandler | PageDataDefault;
export interface PageNavigation {
    currentPageIndex: number;
    currentPageData: PagedOnboardingDialogPageData;
    isCurrentPageModelValid: () => Promise<boolean>;
    canMoveToNext: boolean;
    canMoveToPrevious: boolean;
    moveToNextPage: () => Promise<void>;
    moveToPreviousPage: () => void;
    moveToPageIndex: (pageIndex: number) => void;
    moveToFirstPage: () => void;
}
const usePageNavigation = (pages: PagedOnboardingDialogPageData[]): PageNavigation => {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const currentPageData = pages[currentPageIndex];
    const canMoveToNext = currentPageIndex < pages.length - 1;
    const canMoveToPrevious = currentPageIndex > 0;
    const isCurrentPageModelValid = async () => (currentPageData.isPageModelValid === undefined ? true : await currentPageData.isPageModelValid());
    const moveToNextPage = async () => {
        if (canMoveToNext && (await isCurrentPageModelValid())) {
            await currentPageData.onMovingToNextPage?.();
            setCurrentPageIndex((prevIndex) => prevIndex + 1);
        }
    };
    const moveToPreviousPage = () => {
        if (canMoveToPrevious) {
            setCurrentPageIndex((prevIndex) => prevIndex - 1);
        }
    };
    const moveToFirstPage = () => {
        setCurrentPageIndex(0);
    };
    const moveToPageIndex = (pageIndex: number) => {
        if (pageIndex >= 0 && pageIndex < pages.length) {
            setCurrentPageIndex(pageIndex);
        }
    };
    useEffect(() => {
        currentPageData.onPageDisplayed?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageIndex]);
    return {
        currentPageIndex,
        currentPageData,
        isCurrentPageModelValid,
        canMoveToNext,
        canMoveToPrevious,
        moveToNextPage,
        moveToPageIndex,
        moveToPreviousPage,
        moveToFirstPage,
    };
};
/**
 * This adds basic paging to the OnboardingDialog component.  This supports props for OnboardingDialog changing for each page, which doesn't cleanly fit into the existing standard OnboardingDialog/OnboardingDialogLayout pattern.
 * After a couple of iterations I have decided to keep this simple, and let any future users sculpt it into a more powerful component as needed.
 * Things like per-page actions, or extra render context for each page can be easily added if needed.
 * See areas/infrastructure/components/MachineSettings/Endpoints/KubernetesTentacle/ConfigurationDialog.tsx for an example of its use.
 * @param props
 */
export const PagedOnboardingDialog = forwardRef<PageNavigation, PagedOnboardingDialogProps>((props, ref) => {
    const pageNavigation = usePageNavigation(props.pages);
    useImperativeHandle(ref, () => ({
        ...pageNavigation,
    }), [pageNavigation]);
    const closeDialog = async (isCancel: boolean = false) => {
        if (isCancel || (await pageNavigation.isCurrentPageModelValid())) {
            pageNavigation.currentPageData.onCloseDialog?.(isCancel);
            props.onClosingDialog?.();
            props.close(isCancel);
            pageNavigation.moveToFirstPage();
        }
    };
    const commonActions = () => {
        const showCloseButton = pageNavigation.currentPageData.alwaysShowCloseButton;
        const onlyShowCloseButton = pageNavigation.currentPageData.onlyShowCloseButton;
        const showBackButton = !onlyShowCloseButton && !props.hidePreviousButton && !pageNavigation.currentPageData.hideBackButton && pageNavigation.canMoveToPrevious;
        const showCancelButton = !showCloseButton && (!showBackButton || pageNavigation.currentPageData.alwaysShowCancelButton) && !pageNavigation.currentPageData.hideCancelButton;
        const backButtonImportance = showBackButton && showCancelButton ? "secondary" : "quiet";
        const cancelButtonImportance = (showBackButton || pageNavigation.currentPageData?.customSecondaryButton) && showCancelButton ? "quiet" : "secondary";
        const showNextButton = !onlyShowCloseButton && pageNavigation.canMoveToNext && !pageNavigation.currentPageData.customPrimaryButton;
        const showCompleteButton = !onlyShowCloseButton && !pageNavigation.canMoveToNext && !props.hideCompleteButton && (props.completeButtonText || !showCloseButton);
        return (<>
                {showCancelButton && <Button label="Cancel" importance={cancelButtonImportance} onClick={() => closeDialog(true)}/>}
                {showBackButton && <Button label="Back" importance={backButtonImportance} onClick={() => pageNavigation.moveToPreviousPage()}/>}
                {/* Close dialog */}
                {showCloseButton && <Button label="Close" importance="secondary" onClick={() => closeDialog(true)}/>}
                {pageNavigation.currentPageData.customSecondaryButton ? pageNavigation.currentPageData.customSecondaryButton(pageNavigation) : null}
                {showNextButton && (<Button label={pageNavigation.currentPageData.nextButtonText || "Next"} importance="primary" onClick={async () => await pageNavigation.moveToNextPage()} disabled={pageNavigation.currentPageData.disableNextButton}/>)}
                {/* Close dialog and redirect */}
                {showCompleteButton && <Button label={props.completeButtonText || "Close"} importance="secondary" onClick={() => closeDialog()}/>}
                {!!pageNavigation.currentPageData.customPrimaryButton &&
                (typeof pageNavigation.currentPageData.customPrimaryButton === "function" ? pageNavigation.currentPageData.customPrimaryButton(pageNavigation) : pageNavigation.currentPageData.customPrimaryButton)}
            </>);
    };
    const getHelpPanelDetailsForPage = () => ({
        helpPanelContent: pageNavigation.currentPageData.helpPanelContent ?? props.helpPanelContent,
        helpPanelImage: pageNavigation.currentPageData.helpPanelImage ?? props.helpPanelImage,
        contentPanelHeight: pageNavigation.currentPageData.helpPanelHeight ?? props.contentPanelHeight,
        helpPanelHidden: pageNavigation.currentPageData.helpPanelHidden ?? props.helpPanelHidden,
        helpPanelShowToggle: !(pageNavigation.currentPageData.helpPanelHidden ?? props.helpPanelHidden),
    });
    const getTitleForPage = () => {
        const title = pageNavigation.currentPageData.title ?? props.defaultTitle;
        if (typeof title === "string") {
            return (<div className={titleStyles.dialogTitle}>
                    <div className={titleStyles.titleContainer}>
                        <h2 className={titleStyles.title}>{title}</h2>
                    </div>
                </div>);
        }
        else {
            return title;
        }
    };
    return (<OnboardingDialog open={props.open} close={props.close} name={pageNavigation.currentPageData.name} {...getHelpPanelDetailsForPage()}>
            <OnboardingDialogLayout actions={commonActions()} onCloseDialog={() => closeDialog(true)} title={getTitleForPage()} busy={pageNavigation.currentPageData.isBusy}>
                {props.showProgressTracker && <PagedOnboardingDialogProgressTracker pages={props.pages} pageNavigation={pageNavigation}/>}
                <div style={{ overflow: "auto", height: "100%" }}>{pageNavigation.currentPageData.render() || null}</div>
            </OnboardingDialogLayout>
        </OnboardingDialog>);
});
const titleStyles = {
    dialogTitle: css({
        display: "flex",
        flexWrap: "wrap",
        gap: space[12],
        alignItems: "center",
    }),
    titleContainer: css({
        "@media (max-width: 600px)": {
            flexBasis: "100%",
        },
    }),
    title: css({
        color: themeTokens.color.text.primary,
        font: text.heading.medium,
        margin: 0,
    }),
};
